import type { Encounter, Page, Patient, Platform, PushNotification } from './types'
import { PlatformKind } from './types'
export { Encounter, Page, Patient, Platform, PlatformKind, PushNotification }

import { type Message, MessageKind, on, send } from './messages'

/**
 * An unsubscribe function returned by subscription methods, such as `onPatientChanged()`.
 */
export type Unsubscribe = () => void

/**
 * Indicates if application is running inside of popout
 */
export const inPopout: boolean = !!(window.opener && window.opener !== window)

/**
 * Indicates if application is running inside of iframe
 */
export const inIframe: boolean = !inPopout && window.parent !== window

/**
 * Indicates if application is running inside of Bridge
 */
export const inBridge: boolean = (window.name + '').includes('bridge_')

/**
 * The Bridge SDK version.
 */
export const version = '2.9.3'

/**
 * Return the current page HTML and href.
 */
export function getPage(deep: boolean = false): Promise<Page> {
	return new Promise((resolve) => {
		if (!inBridge) resolve(null)
		const off = on(MessageKind.GET_PAGE, ({ data }) => {
			off()
			resolve(data as Page)
		})
		sendToParent(MessageKind.GET_PAGE, { deep })
	})
}

/**
 * Get the current patient being displayed. This is typically used on application
 * initialization and thereafter onPatientChanged() is used to listen for additional
 * changes.
 */
export async function getPatient(): Promise<Patient> {
	return new Promise(resolve => {
		if (!inBridge) resolve(null)
		const off = on(MessageKind.GET_PATIENT_INFO, ({ data }) => {
			off()
			resolve(data as Patient)
		})
		sendToParent(MessageKind.GET_PATIENT_INFO)
	})
}

export async function getPlatform(): Promise<Platform> {
	return new Promise(resolve => {
		if (!inBridge) resolve(null)
		const off = on(MessageKind.GET_PLATFORM, ({ data }) => {
			off()
			resolve(data as Platform)
		})
		sendToParent(MessageKind.GET_PLATFORM)
	})
}

/**
 * Sets the badge count on the tile. Setting the value to 0 will cause it to go away.
 */
export function setBadgeCount(count: number = 0): void {
	sendToParent(MessageKind.SET_BADGE_COUNT, count)
}

/**
 * Shows tile. Controlled by a Smart Tile based on the
 * information it receives through the available hooks such as "onPatientChanged()"
 */
export function showTile() {
	sendToParent(MessageKind.SHOW_TILE)
}

/**
 * Hide tile. Controlled by a Smart Tile based on the
 * information it receives through the available hooks such as "onPatientChanged()"
 */
export function hideTile() {
	sendToParent(MessageKind.HIDE_TILE)
}

/**
 * Enables tile allowing event handling. Controlled by a Smart Tile based on the
 * information it receives through the available hooks such as "onPatientChanged()"
 */
export function enableTile() {
	sendToParent(MessageKind.ENABLE_TILE)
}

/**
 * Disables tile preventing user events. Controlled by a Smart Tile based on the
 * information it receives through the available hooks such as "onPatientChanged()"
 */
export function disableTile() {
	sendToParent(MessageKind.DISABLE_TILE)
}

/**
 * Smart tile request to capture user events. Bridge will not open an application while
 * tile is capturing user events.
 */
export function captureUserEvents() {
	sendToParent(MessageKind.CAPTURE_USER_EVENTS)
}

/**
 * Smart tile returns the capturing user events back to Bridge. Bridge can only open
 * application when Bridge is capturing user events.
 */
export function releaseUserEvents() {
	sendToParent(MessageKind.RELEASE_USER_EVENTS)
}

/**
 * Closes app making the request. The tile can also call this and it will close the linked application.
 */
export function closeApp() {
	sendToParent(MessageKind.CLOSE_APP)
}

/**
 * Bridge will add the notification to the notifications array
 * with icon representing the application
 */
export function pushNotification(notification: PushNotification): void {
	sendToParent(MessageKind.PUSH_NOTIFICATION, notification)
}

export function getOpenEncounter(): Promise<Encounter | null> {
	return new Promise((resolve) => {
		if (!inBridge) resolve(null)
		const off = on(MessageKind.GET_OPEN_ENCOUNTER, ({ data }) => {
			off()
			resolve(data as Encounter)
		})
		sendToParent(MessageKind.GET_OPEN_ENCOUNTER)
	})
}

/**
 * Subscribe to the open encounter change event.
 * When the user navigates to an open encounter page, `cb` is called with the encounter information.
 * When the user navigates away from an open encounter page, `cb` is called with `null`.
 *
 * @param cb - The callback function to be called when the open encounter changes.
 */
export function onOpenEncounterChanged(cb: (encounter: Encounter) => void): Unsubscribe {
	return on(MessageKind.SET_OPEN_ENCOUNTER, (msg: Message) => cb(msg.data as Encounter))
}

/**
 * Subscribe to the patient change event.
 * When the user navigates to a patient page, `cb` is called with the patient information.
 * When the user navigates away from a patient page, `cb` with `null`.
 *
 * @param cb - The callback function to be called when the patient changes.
 */
export function onPatientChanged(cb: (patient: Patient) => void): Unsubscribe {
	return on(MessageKind.SET_PATIENT_INFO, (msg: Message) => cb(msg.data as Patient))
}

function sendToParent(messageKind: MessageKind, data?: unknown) {
	// window.parent for iframe
	let parentWindow = window.parent
	// if window.opener exists then it is a popup and needs to use the opener
	if (window.opener) {
		parentWindow = window.opener
	}
	if (window === parentWindow) {
		console.warn('Cannot post message to self. No parent window found.')
		return
	}
	if(!window.name) {
		console.warn('No app id assigned. Cannot post request.')
		return
	}
	// sends the appId from the window name with message to Bridge
	send(parentWindow, messageKind, { appId: window.name, data, sdkVersion: version })
}
